import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Input from './components/input/input';
import Dropdown from './components/dropdown/dropdown';
import BackdropOverlay from './components/dropdown/backdropOverlay/backdropOverlay';
import useFetch from './hooks/useFetch';
import useClickOutside from './hooks/useClickOutside';
import useHoverOutside from './hooks/useHoverOutside';
import useResizeListener from './hooks/useResizeListener';
import useBodyScrollLock from './hooks/useBodyScrollLock';
import getPressedKey, { KeyboardMap } from '../../../../helpers/getPressedKey';

function SearchApp(): React.ReactElement {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const data = useFetch(input, open);
  const MAX_INPUT_LENGTH = 40;

  const { current: searchAppElement } = useRef(document.getElementById('search-app'));
  const { current: dropdownElement } = useRef(document.getElementById('search-app-dropdown'));
  const { current: backdropOverlayElement } = useRef(document.getElementById('search-app-overlay'));

  const inputRef = useRef<HTMLInputElement>(null);

  const { innerHeight, innerWidth } = useResizeListener();

  const canDisplayDropdown = useMemo<boolean>(
    () =>
      open &&
      !!data &&
      data.status !== 'ERROR' &&
      data.successfull === true &&
      (data.data.suggestion?.searchTerms.length > 0 || data.data.suggestion?.products.length > 0),
    [open, data]
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInput(e.target.value.substring(0, MAX_INPUT_LENGTH).trim());
  }

  function handleFocus() {
    setOpen(true);
    searchAppElement?.style.setProperty('transform', `translateY(${0}px)`);
  }

  function handleEsc(e: React.KeyboardEvent) {
    if (getPressedKey(e) === KeyboardMap.ESCAPE) {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  function handleClose() {
    setOpen(false);
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const insideSearchApp =
      (e.relatedTarget as HTMLElement)?.closest('#search-app-dropdown') ||
      (e.relatedTarget as HTMLElement)?.closest('#search-app');
    if (!insideSearchApp) {
      handleClose();
    }
  }

  useClickOutside(open, setOpen);
  useHoverOutside(open, setOpen);
  useBodyScrollLock(canDisplayDropdown, innerWidth);

  return (
    <>
      <Input
        ref={inputRef}
        open={open}
        canDisplayDropdown={canDisplayDropdown}
        innerWidth={innerWidth}
        handleBlur={handleBlur}
        handleChange={handleChange}
        handleEsc={handleEsc}
        handleFocus={handleFocus}
        maxLength={MAX_INPUT_LENGTH}
      />
      {dropdownElement
        ? createPortal(
            <Dropdown
              data={data}
              open={open}
              canDisplayDropdown={canDisplayDropdown}
              innerHeight={innerHeight}
              innerWidth={innerWidth}
              handleBlur={handleBlur}
              handleClose={handleClose}
              handleEsc={handleEsc}
            />,
            dropdownElement
          )
        : null}
      {backdropOverlayElement
        ? createPortal(
            <BackdropOverlay canDisplayDropdown={canDisplayDropdown} handleClose={handleClose} />,
            backdropOverlayElement
          )
        : null}
    </>
  );
}

export default SearchApp;
